import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Button from '../Button/Button';
import Dialog from '../Dialog/Dialog';

import styles from './ThumbnailPickerModal.module.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  videoFile: File | null;
  onSave: (thumbnail: string | File) => void;
  initialThumbnail?: string;
};

const ThumbnailPickerModal = ({ open, onClose, videoFile, onSave, initialThumbnail }: Props) => {
  const { t } = useTranslation('upload');
  const [frames, setFrames] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const isScrubbing = useRef(false);

  useEffect(() => {
    if (open && videoFile && frames.length === 0) {
      const frameCount = window.innerWidth <= 768 ? 8 : 12;
      extractFrames(videoFile, frameCount);
    }
  }, [open, videoFile, frames.length]);

  useEffect(() => {
    if (!open) {
      setFrames([]);
    }
    setSelectedIndex(0);
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open || frames.length === 0) { return; }

      if (e.key === 'ArrowLeft') {
        setSelectedIndex((prev) => {
          return prev < 0 ? 0 : Math.max(0, prev - 1);
        });
      } else if (e.key === 'ArrowRight') {
        setSelectedIndex((prev) => {
          return prev < 0 ? 0 : Math.min(frames.length - 1, prev + 1);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, frames.length]);

  const extractFrames = async (file: File, count: number) => {
    setLoading(true);
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.playsInline = true;

      await new Promise((resolve) => {
        video.onloadedmetadata = () => resolve(null);
      });

      const duration = video.duration;
      const interval = duration / (count + 1);
      const extractedFrames: string[] = [];

      for (let i = 1; i <= count; i++) {
        video.currentTime = i * interval;
        await new Promise((resolve) => {
          video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
            extractedFrames.push(canvas.toDataURL('image/jpeg', 0.1));
            resolve(null);
          };
        });
      }

      setFrames(extractedFrames);
      URL.revokeObjectURL(video.src);
    } catch (error) {
      console.error('Error extracting frames:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) { return; }

    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartX.current;

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        setSelectedIndex((prev) => Math.max(0, prev - 1));
      } else {
        setSelectedIndex((prev) => Math.min(frames.length - 1, prev + 1));
      }
    }
    touchStartX.current = null;
  };

  const handleScrub = (clientX: number, clientY: number) => {
    const element = document.elementFromPoint(clientX, clientY);
    const thumb = element?.closest(`[data-index]`);
    if (thumb instanceof HTMLElement) {
      const index = Number.parseInt(thumb.dataset.index || '0', 10);
      if (index !== selectedIndex) {
        setSelectedIndex(index);
      }
    }
  };

  const handleSave = () => {
    if (selectedIndex >= 0 && frames[selectedIndex]) {
      onSave(frames[selectedIndex]);
    }
    onClose();
  };

  const currentPreview = frames[selectedIndex] ?? (loading ? undefined : initialThumbnail);

  return (
    <Dialog size="large" open={open} onClose={onClose} role="dialog" shouldNotCloseButton={true}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('thumbnail_picker.title')}</h2>
        </div>

        <div className={styles.body}>
          <div className={styles.previewContainer} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            {currentPreview ? <img src={currentPreview} alt="Cover Preview" className={styles.mainPreview} /> : <div className={styles.placeholder} />}
          </div>

          <div
            className={styles.frameStrip}
            onMouseDown={(e) => {
              isScrubbing.current = true;
              handleScrub(e.clientX, e.clientY);
            }}
            onMouseMove={(e) => {
              if (isScrubbing.current) {
                handleScrub(e.clientX, e.clientY);
              }
            }}
            onMouseUp={() => {
              isScrubbing.current = false;
            }}
            onMouseLeave={() => {
              isScrubbing.current = false;
            }}
            onTouchStart={(e) => {
              isScrubbing.current = true;
              const touch = e.touches[0];
              handleScrub(touch.clientX, touch.clientY);
            }}
            onTouchMove={(e) => {
              if (isScrubbing.current) {
                const touch = e.touches[0];
                handleScrub(touch.clientX, touch.clientY);
              }
            }}
            onTouchEnd={() => {
              isScrubbing.current = false;
            }}
          >
            {loading ? (
              <div className={styles.loadingFrames}>{t('thumbnail_picker.loading_frames')}</div>
            ) : (
              frames.map((frame, index) => (
                <div
                  key={frame}
                  data-index={index}
                  className={classNames(styles.frameThumb, { [styles.selected]: selectedIndex === index })}
                  onClick={() => {
                    setSelectedIndex(index);
                  }}
                >
                  <img src={frame} alt={`Frame ${index}`} />
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <Button label={t('thumbnail_picker.cancel')} onClick={onClose} variant="mediaOverlay" color="secondary" className={styles.cancelBtn} />
          <Button label={t('thumbnail_picker.save')} onClick={handleSave} variant="contained" color="primary" className={styles.saveBtn} />
        </div>
      </div>
    </Dialog>
  );
};

export default ThumbnailPickerModal;
